export const CHECKOUT_URL = "https://shoulder-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/kLs4pZ1tEsZgcoYUemUx7o/MOCKUP_SHOULDER-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/opfK1FJyPqQVnxkE2nfjY1/RYAN_CARTER_-_SPINE.png",
};

export const problemQuestions = [
  "Which questions should I prioritize during the initial interview?",
  "Should I focus on active movement, passive mobility, strength, or specific examination procedures?",
  "How do I interpret findings when several conditions could explain the symptoms?",
  "What should I investigate when the examination doesn’t support my initial hypothesis?",
  "When should I consider a cervical contribution or additional medical evaluation?",
];

export const deliverables = [
  {
    figure: "6",
    title: "Comprehensive volumes",
    text: "Six practical volumes covering the initial interview, screening considerations, physical examination, clinical reasoning, differential assessment, and educational case studies.",
  },
  {
    figure: "5",
    title: "Specialized bonuses",
    text: "Five complementary resources, including assessment checklists, screening worksheets, examination references, clinical reasoning templates, and case-review exercises.",
  },
  {
    figure: "1",
    title: "Structured assessment workflow",
    text: "A practical educational framework for organizing the initial interview, relevant screening considerations, physical examination, and interpretation of findings.",
    note: "This workflow is an organizational reference, not a guarantee that every patient can be assessed within a particular time.",
  },
];

export const beforeItems = [
  "You sometimes struggle to decide which examination procedures are most relevant to the patient’s presentation.",
  "You spend too much time searching for information across different resources.",
  "You find it challenging to connect subjective symptoms with physical examination findings.",
  "You want a more systematic approach to considering alternative explanations for shoulder pain.",
  "You struggle to organize competing clinical hypotheses.",
  "You sometimes approach unfamiliar presentations without a consistent process for identifying what information is still missing.",
];

export const afterItems = [
  "You have a structured reference for organizing the initial interview.",
  "You can document relevant symptoms, functional limitations, and examination findings more systematically.",
  "You have dedicated educational resources for reviewing potentially concerning presentations.",
  "You can organize your examination findings by category.",
  "You have worksheets for making your clinical reasoning more transparent.",
  "You can practice applying your knowledge through educational case scenarios.",
];

export const situations = [
  {
    title: "Your patient reports shoulder pain during overhead movements.",
    text: "You need to organize the history, relevant examination findings, and functional limitations without jumping to conclusions based on a single symptom or test.",
  },
  {
    title: "You want a more systematic approach to rotator cuff assessment.",
    text: "You understand the importance of assessing movement and strength, but you want a clearer framework for organizing the examination and interpreting findings in context.",
  },
  {
    title: "You collect findings but struggle to connect them.",
    text: "You ask questions, assess movement, and perform examination procedures. However, turning that information into a coherent clinical picture can take time.",
  },
  {
    title: "You want to improve your clinical documentation.",
    text: "You want your notes to reflect the patient’s presentation, relevant examination findings, clinical reasoning, and decisions about further assessment.",
  },
  {
    title: "You sometimes feel unsure about what to investigate next.",
    text: "When findings don’t fit the expected pattern, you want a structured way to identify missing information and consider alternative explanations.",
  },
  {
    title: "You want a repeatable framework for your initial assessments.",
    text: "You don’t want to start from scratch with every unfamiliar presentation. You want an organized reference that supports a consistent process without replacing your professional judgment.",
  },
];

export const volumes = [
  {
    label: "Volume I",
    color: "#1F6FEB",
    title: "The Shoulder Initial Assessment Framework",
    intro: "Learn how to organize the initial interview and gather relevant information before proceeding with your physical examination.",
    items: [
      "A structured approach to the presenting complaint.",
      "Questions about symptom onset, duration, and progression.",
      "Relevant injury mechanisms and previous episodes.",
      "Factors that aggravate or ease symptoms.",
      "Functional limitations and patient-reported concerns.",
      "Relevant medical history and contextual information.",
      "Work, sport, and everyday activity considerations.",
      "Cervical symptoms and relevant neurological complaints.",
      "How to organize subjective findings.",
      "A framework for documenting the initial assessment.",
    ],
    goal: "Build a consistent structure for gathering and organizing information before deciding what requires further investigation.",
  },
  {
    label: "Volume II",
    color: "#D93636",
    title: "The Shoulder Screening & Referral Guide",
    intro: "Recognizing potentially concerning findings is an essential part of responsible assessment. This volume focuses on organizing relevant warning signs and understanding when further medical evaluation may be necessary.",
    items: [
      "Important historical findings and changes in symptom presentation.",
      "Relevant trauma and suspicious deformity.",
      "Potential warning signs associated with serious underlying conditions.",
      "Symptoms that may suggest systemic involvement.",
      "Considerations involving cardiovascular or visceral sources of shoulder-region symptoms.",
      "Relevant neurological symptoms and cervical considerations.",
      "General principles for documenting concerns and considering referral.",
      "Situations that may warrant urgent medical evaluation.",
      "The limitations of individual screening findings.",
    ],
    note: "The material should be interpreted alongside current clinical guidelines, local referral procedures, and the patient’s complete presentation.",
    goal: "Develop a more systematic approach to screening and recognizing when routine physiotherapy assessment may not be sufficient.",
  },
  {
    label: "Volume III",
    color: "#1D4FB8",
    title: "The Shoulder Physical Examination Atlas",
    intro: "Organize the physical examination using a practical educational reference covering movement, function, strength, and relevant examination procedures.",
    items: [
      "Observation of the shoulder complex.",
      "Active and passive range-of-motion assessment.",
      "Strength assessment and relevant functional observations.",
      "Examination considerations for the rotator cuff.",
      "Selected orthopedic examination procedures.",
      "Assessment of relevant cervical contributions.",
      "Documentation of positive, negative, and inconclusive findings.",
      "Considerations for selecting examination procedures.",
      "Limitations of individual special tests.",
      "The role of standardized patient-reported outcome measures.",
    ],
    goal: "Help you organize the physical examination and interpret findings in the context of the patient’s overall presentation.",
  },
  {
    label: "Volume IV",
    color: "#14935A",
    title: "Shoulder Clinical Reasoning & Differential Assessment",
    intro: "Collecting information is only one part of the assessment. You also need to understand how different findings relate to one another. This volume focuses on organizing your clinical reasoning.",
    items: [
      "How to synthesize subjective and objective findings.",
      "How to formulate and revise working hypotheses.",
      "How to consider alternative explanations for shoulder pain.",
      "How to recognize relevant cervical contributions.",
      "How to identify missing information.",
      "How to interpret findings in the context of the complete presentation.",
      "How to document clinical uncertainty.",
      "How to recognize when additional assessment may be necessary.",
      "How to avoid relying on a single examination result.",
    ],
    goal: "Develop a more coherent approach to clinical reasoning instead of relying on a single symptom or examination result.",
  },
  {
    label: "Volume V",
    color: "#E8650C",
    title: "Rotator Cuff, Shoulder Stiffness & Instability Assessment",
    intro: "Shoulder pain can involve different presentations, overlapping symptoms, and varying levels of clinical complexity. This volume helps you organize your understanding of selected shoulder presentations while recognizing the limitations of assessment findings.",
    items: [
      "Educational assessment frameworks for rotator cuff-related shoulder pain.",
      "Relevant historical and examination findings.",
      "Considerations involving restricted shoulder movement.",
      "Assessment considerations for suspected shoulder instability.",
      "Functional limitations and activity-related symptoms.",
      "Selection and interpretation of relevant examination procedures.",
      "How to organize competing clinical hypotheses.",
      "Factors that may suggest the need for further medical investigation.",
      "Documentation frameworks for clinical decisions.",
      "The role of professional scope and current clinical guidelines.",
    ],
    goal: "Organize the information available to you and identify appropriate next steps while respecting the limits of clinical assessment.",
  },
  {
    label: "Volume VI",
    color: "#6D3FD1",
    title: "The Shoulder Clinical Casebook",
    intro: "Practice applying the assessment framework through educational case scenarios. Each case is designed to help you review how information is gathered, organized, and interpreted.",
    items: [
      "Hypothetical patient presentations.",
      "Examples of subjective histories.",
      "Selected physical examination findings.",
      "Cases involving different shoulder symptom patterns.",
      "Educational scenarios involving restricted movement.",
      "Presentations involving suspected rotator cuff-related symptoms.",
      "Cases involving possible cervical contributions.",
      "Questions to guide clinical interpretation.",
      "Opportunities to identify missing information.",
      "Example reasoning pathways.",
      "Reflection exercises to identify gaps in your reasoning.",
    ],
    note: "All cases are educational scenarios, not individualized patient assessments or definitive diagnostic protocols.",
    goal: "Practice organizing clinical information before applying your knowledge in professional settings.",
  },
];

export const bonuses = [
  {
    label: "Bonus 1",
    color: "#1F6FEB",
    title: "The Shoulder Initial Assessment Checklist",
    image: "https://cdn.magicpatterns.com/uploads/u1NkBMm2e3B3AMwy4r8ZPp/BONUS_1_SHOULDER-EN.png",
    description: "A structured checklist to help you organize the main components of an initial shoulder assessment.",
    listTitle: "Use it to review",
    items: [
      "Presenting complaint and symptom history.",
      "Relevant medical background.",
      "Injury mechanism and previous episodes.",
      "Functional limitations.",
      "Important screening questions.",
      "Examination planning.",
      "Documentation and follow-up considerations.",
    ],
    note: "Designed to support a consistent process without replacing individualized clinical judgment.",
  },
  {
    label: "Bonus 2",
    color: "#D93636",
    title: "The Shoulder Screening Worksheet",
    image: "https://cdn.magicpatterns.com/uploads/2yJrMtgDqAkBum2zKoW8Jw/BONUS_2_SHOULDER-EN.png",
    description: "A practical worksheet for organizing relevant symptoms, historical findings, concerning changes, and decisions about further evaluation.",
    listTitle: "Use it to document",
    items: [
      "Relevant patient-reported symptoms.",
      "Important historical findings.",
      "Changes in symptom presentation.",
      "Neurological concerns.",
      "Potential systemic or non-musculoskeletal features.",
      "Reasons to consider additional medical evaluation.",
      "Referral and escalation considerations.",
    ],
    note: "The worksheet supports documentation and clinical reasoning. It is not a standalone screening test or a substitute for emergency procedures.",
  },
  {
    label: "Bonus 3",
    color: "#14935A",
    title: "The Shoulder Examination Quick Reference",
    image: "https://cdn.magicpatterns.com/uploads/4wBgcfooka2AaHfYAsnN6M/BONUS_3_SHOULDER-EN.png",
    description: "A compact reference to help you organize the main components of a shoulder examination.",
    listTitle: "Review considerations involving",
    items: [
      "Observation and functional movement.",
      "Active and passive range of motion.",
      "Strength assessment.",
      "Rotator cuff examination.",
      "Selection of relevant examination procedures.",
      "Cervical screening considerations.",
      "Documentation of findings.",
    ],
    note: "Use it to structure your review of the examination process without treating any single test as definitive.",
  },
  {
    label: "Bonus 4",
    color: "#E8650C",
    title: "The Shoulder Clinical Reasoning Matrix",
    image: "https://cdn.magicpatterns.com/uploads/2hhvi6VPiW1TQbBUbo2MDS/BONUS_4_SHOULDER-EN.png",
    description: "A structured worksheet for organizing the information collected during an assessment.",
    listTitle: "Use the matrix to record",
    items: [
      "Relevant subjective findings.",
      "Physical examination results.",
      "Potential working hypotheses.",
      "Findings that support or challenge each hypothesis.",
      "Information that remains missing.",
      "Alternative explanations to consider.",
      "Reasons to consider further investigation or referral.",
    ],
    note: "The objective is to make your reasoning more transparent, organized, and easier to document.",
  },
  {
    label: "Bonus 5",
    color: "#6D3FD1",
    title: "The Shoulder Case Review Workbook",
    image: "https://cdn.magicpatterns.com/uploads/7RDhZVGRv71iLxrqYuCxFV/BONUS_5_SHOULDER-EN.png",
    description: "An educational workbook that helps you practice applying the assessment framework to hypothetical clinical presentations.",
    listTitle: "Each exercise encourages you to",
    items: [
      "Review the presenting complaint.",
      "Identify relevant information.",
      "Organize examination findings.",
      "Recognize potentially concerning features.",
      "Consider alternative explanations.",
      "Identify missing information.",
      "Explain your reasoning.",
      "Reflect on the limitations of your conclusions.",
    ],
    note: "Designed to reinforce learning through practice rather than passive reading alone.",
  },
];

export const pillars = [
  { title: "Structure", text: "Organize your initial interview and gather relevant clinical information." },
  { title: "Screening", text: "Review important warning signs and understand when additional medical evaluation may be necessary." },
  { title: "Examination", text: "Use a structured reference to organize relevant physical examination components." },
  { title: "Clinical reasoning", text: "Bring the findings together while considering alternative explanations and recognizing uncertainty." },
  { title: "Practice", text: "Use educational case scenarios to review how the different elements work together." },
];

export const offerIncludes = {
  volumes: [
    "The Shoulder Initial Assessment Framework",
    "The Shoulder Screening & Referral Guide",
    "The Shoulder Physical Examination Atlas",
    "Shoulder Clinical Reasoning & Differential Assessment",
    "Rotator Cuff, Shoulder Stiffness & Instability Assessment",
    "The Shoulder Clinical Casebook",
  ],
  bonuses: [
    "The Shoulder Initial Assessment Checklist",
    "The Shoulder Screening Worksheet",
    "The Shoulder Examination Quick Reference",
    "The Shoulder Clinical Reasoning Matrix",
    "The Shoulder Case Review Workbook",
  ],
  perks: [
    "Instant digital access after payment confirmation.",
    "All six volumes.",
    "All five specialized bonuses.",
    "Lifetime access to purchased files.",
    "No recurring subscription.",
  ],
};

export const guaranteeItems = [
  "Review all six volumes.",
  "Explore the assessment frameworks.",
  "Examine the screening reference.",
  "Review the physical examination resources.",
  "Use the worksheets to organize your learning.",
  "Work through the educational case scenarios.",
];

export const testimonials = [
  {
    name: "Elena R.",
    role: "Physiotherapist",
    country: "Spain",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/a6ccf325-7be1-4649-ae62-c86086722c67.jpg",
    headline: "I needed a clearer way to organize my shoulder assessments.",
    quote: "What I appreciate about this approach is the way it brings the interview, physical examination, and clinical reasoning together. Instead of reviewing isolated tests, the structure encourages me to think about how the different pieces of information relate to one another.",
  },
  {
    name: "Jason M.",
    role: "Physical Therapist",
    country: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/63cd51b3-5cc3-4396-bdfb-5e8452fffb3b.jpg",
    headline: "The examination references make reviewing the material easier.",
    quote: "I was looking for an educational resource that would help me organize the different components of a shoulder examination. Having the information arranged by topic makes it easier to revisit the concepts I want to review.",
  },
  {
    name: "Chloe W.",
    role: "Physiotherapy Student",
    country: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/72a48a47-ad29-4e71-9a99-ac712cbf54ff.jpg",
    headline: "I like the focus on clinical reasoning, not just tests.",
    quote: "Knowing how to perform an examination procedure is only one part of the process. I find the emphasis on interpreting findings in context and considering alternative explanations particularly useful as a learning approach.",
  },
  {
    name: "Liam H.",
    role: "Sports Physiotherapist",
    country: "Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/879b9a50-9842-4e0f-8a83-040d26a94c30.jpg",
    headline: "The worksheets provide a practical way to organize my thoughts.",
    quote: "The assessment checklist and clinical reasoning matrix offer a useful structure for reviewing what information has been collected, what the findings might suggest, and what still needs to be considered.",
  },
  {
    name: "Grace L.",
    role: "Physiotherapist",
    country: "Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/e00af01c-38dc-4d6f-acbe-80eec61b8f14.jpg",
    headline: "The case-based approach makes the learning more practical.",
    quote: "I prefer learning resources that encourage me to think through a presentation instead of simply memorizing information. Hypothetical cases provide an opportunity to practice organizing findings and explaining the reasoning behind different interpretations.",
  },
  {
    name: "Carlos R.",
    role: "Physiotherapist",
    country: "Mexico",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/2083184c-54bc-4ef7-b94e-6c0c04fcb3be.jpg",
    headline: "The screening guide gave my documentation a clear structure.",
    quote: "I wanted a consistent way to record warning signs and explain why I considered a referral. Having the screening worksheet next to the reasoning matrix helps me keep my notes organized and my decisions easier to justify.",
  },
];

export const faqs = [
  {
    q: "How will I receive the material?",
    a: "After your payment is confirmed, you’ll receive instructions for accessing your digital purchase. The six volumes and five bonuses will be provided in the formats specified at checkout.",
  },
  {
    q: "Who is this collection for?",
    a: "Shoulder Assessment Blueprint™ is designed for physiotherapists, physical therapists, and physical therapy students who want an organized educational reference for reviewing shoulder assessment concepts.",
  },
  {
    q: "Is this suitable for practicing physiotherapists?",
    a: "Yes. The collection is intended to complement existing professional knowledge and provide structured educational references for reviewing the assessment process. It does not replace clinical experience, continuing professional education, or current clinical guidelines.",
  },
  {
    q: "Is this suitable for students?",
    a: "Yes. Students can use the material to review assessment concepts, organize their study, and practice clinical reasoning through hypothetical cases. It should be used alongside formal instruction and supervised clinical education.",
  },
  {
    q: "Does this teach me how to diagnose every cause of shoulder pain?",
    a: "No. Shoulder pain can have multiple causes, and assessment findings have limitations. The collection provides educational frameworks for organizing information and considering relevant possibilities. It does not guarantee diagnostic accuracy or replace an individualized assessment by an appropriately qualified professional.",
  },
  {
    q: "Does it include rotator cuff assessment?",
    a: "Yes. The collection includes educational material on rotator cuff-related presentations, relevant examination considerations, and the interpretation of findings in context. It also covers other selected shoulder presentations, including restricted movement and instability considerations.",
  },
  {
    q: "Does it include red flags and referral considerations?",
    a: "Yes. The screening volume covers relevant concerning features and general considerations for additional medical evaluation. It is an educational resource, not an emergency protocol or a substitute for current local referral procedures.",
  },
  {
    q: "Can I use the material with patients?",
    a: "You may use the educational references to support your own professional learning and assessment organization, within your qualifications and scope of practice. The material does not replace individualized patient assessment, clinical judgment, or applicable professional standards.",
  },
  {
    q: "Do I need special software?",
    a: "The collection is digital. Please refer to the delivery information at checkout for the specific file formats and any software requirements.",
  },
  {
    q: "Is this a subscription?",
    a: "No. The offer is a one-time payment of $27. There are no recurring subscription charges associated with this purchase.",
  },
  {
    q: "Will I have lifetime access?",
    a: "The offer includes lifetime access to the purchased files, subject to the terms presented at checkout and the platform’s applicable access policies.",
  },
  {
    q: "What if I’m not satisfied?",
    a: "The offer includes a seven-day money-back guarantee, subject to the published refund policy. If the collection doesn’t meet your educational needs, contact us within the applicable guarantee period and follow the refund procedure provided with your purchase.",
  },
];

export const recentBuyers = [
  { name: "María González", country: "Mexico" },
  { name: "Santiago Pérez", country: "Argentina" },
  { name: "Valentina Rojas", country: "Chile" },
  { name: "Andrés Ramírez", country: "Colombia" },
  { name: "Lucía Fernández", country: "Spain" },
  { name: "Diego Castillo", country: "Peru" },
  { name: "Camila Torres", country: "Uruguay" },
  { name: "Javier Morales", country: "Ecuador" },
  { name: "Isabella Vargas", country: "Costa Rica" },
  { name: "Mateo Herrera", country: "Dominican Republic" },
  { name: "Sofía Medina", country: "Guatemala" },
  { name: "Gabriel Ortiz", country: "Venezuela" },
  { name: "Daniela Ruiz", country: "Paraguay" },
  { name: "Alejandro Núñez", country: "Bolivia" },
  { name: "Paula Jiménez", country: "Panama" },
];
